import { QAndASides } from 'router/subrouters/DataRoom/models/QAndA';
import { ObjectValues } from 'types';
import { YesNo } from './Fields';
import { EmailOrPhoneRequest } from './AuthBody';
import { DESKTOP_APP_PLATFORM } from './DesktopApp';

export type BaseMixpanelEvent = {
  pageUrl: string;
};

export type LogoutProps = BaseMixpanelEvent;

export type ErrorViewedProps = BaseMixpanelEvent & {
  id: string;
  errorCode: string;
};

export type PageViewedProps = BaseMixpanelEvent & {
  page: string;
};

export type WitnessedProps = {
  userId: string;
  triggererId: string;
  isWitnessedEvent: boolean;
};

export type AhaMomentProps = {
  isOwner: boolean;
  userSide?: QAndASides;
};

export type TimeSpentProps = {
  timeSpentInSeconds: number;
};

export type DocumentEventProps = BaseMixpanelEvent &
  AhaMomentProps &
  TimeSpentProps & {
    documentName: string;
    dataRoomName?: string;
  };

export type WitnessedDocumentViewedEventProps = BaseMixpanelEvent &
  WitnessedProps &
  TimeSpentProps & {
    dataRoomName?: string;
    documentName: string;
  };

export type SearchInitiatedEventProps = BaseMixpanelEvent & {
  searchedTerm: string;
};

export type SecureLinkCreationInitiatedEventProps = BaseMixpanelEvent &
  AhaMomentProps & {
    linkName: string;
    linkAccess: string;
    linkExpiration: string;
    linkProtection: string;
    remoteTermination: string;
    dataRoomId: string;
    dataRoomName?: string;
  };

export type ListingCreationInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dealName: string;
  listingName: string;
  industry: string;
  isPaid: boolean;
  listingPackage: string;
  listingType: string;
  mediaPackage?: string;
  outreach?: string;
};

export type ListingVisitedEventProps = BaseMixpanelEvent & {
  listingName: string;
  dealId: string;
  userSide: QAndASides;
  timeOnPageInSecs: number;
};

export type WitnessedListingVisitedEventProps = BaseMixpanelEvent &
  WitnessedProps & {
    listingName: string;
    dealId: string;
    timeOnPageInSecs: number;
  };

type PhoneMixpanelEventProps = BaseMixpanelEvent & {
  phoneNumber: string;
};

export type MobileVerificationInitiatedEventProps = PhoneMixpanelEventProps;

export type PhoneNumberSetupInitiatedEventProps = PhoneMixpanelEventProps;

export const ENQUIRY_ORIGIN = {
  MANUAL: 'manual',
  ORGANIC: 'organic',
  SECURE_LINK: 'secure link',
  CONTACTS: 'contacts',
};

type EnquiryOrigin = ObjectValues<typeof ENQUIRY_ORIGIN>;

export const MANUAL_ENQUIRY_SUBCATEGORY = {
  NEW_LEAD: 'new lead',
  PREVIOUS_LEAD: 'previous lead',
} as const;

export type ManualEnquirySubcategory = ObjectValues<
  typeof MANUAL_ENQUIRY_SUBCATEGORY
>;

export type EnquiryCreationInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dealName: string;
  enquiryOrigin: EnquiryOrigin;
  manualEnquirySubcategory?: ManualEnquirySubcategory;
};

export type EnquiryWithdrawalInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dealName: string;
  leadId: string;
};

export const SIGNUP_METHOD = {
  EMAIL: 'email',
  PHONE_NUMBER: 'phone_number',
} as const;

export type SignupMethod = ObjectValues<typeof SIGNUP_METHOD>;

export const SIGNUP_SOURCE = {
  ORGANIC: 'organic',
  LEAD_INVITATION: 'lead_invitation',
  BROKER_INVITATION: 'broker_invitation',
  ENTERPRISE_INVITATION: 'enterprise_invitation',
  SECURE_LINK: 'secure_link',
} as const;

export type SignupSource = ObjectValues<typeof SIGNUP_SOURCE>;

export type SignupInitiatedProps = BaseMixpanelEvent & {
  socialSignup: boolean;
  signupSource: SignupSource;
  signupMethod?: SignupMethod;
  email?: string;
  phoneNumber?: string;
};

export type DealCreationInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dealName: string;
};

export type SoftSignalInitiatedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dataRoomId: string;
  documentId: string;
  reaction: string;
};

export type SoftSignalSeenEventProps = BaseMixpanelEvent & {
  dealId: string;
  dataRoomId: string;
  documentId: string;
};

export type DocumentPublishedEventProps = BaseMixpanelEvent &
  AhaMomentProps & {
    documentName: string;
    documentType: string;
    dataRoomId: string;
    dataRoomName: string;
  };

export type QuestionSubmissionInitiatedEventProps = BaseMixpanelEvent &
  AhaMomentProps & {
    questionSubject: string;
    questionContent: string;
    questionPriority?: string;
    addedToFAQ?: boolean;
    assignee?: string;
    buysideBehalf?: string;
    relatedDocument?: string;
  };

export type QuestionReplyInitiatedEventProps = BaseMixpanelEvent &
  AhaMomentProps & {
    questionId: string;
    questionContent: string;
    answerContent: string;
    addedToFAQ: boolean;
    approvalModus: ApprovalModus;
  };

export const ANSWER_REVIEW_OUTPUT = {
  ACCEPTED: 'accepted',
  REJECTED: 'rejected',
} as const;

export type AnswerReviewOutput = ObjectValues<typeof ANSWER_REVIEW_OUTPUT>;

export type AnswerReviewInitiatedEventProps = BaseMixpanelEvent &
  AhaMomentProps & {
    questionId: string;
    questionContent: string;
    answerId: string;
    answerContent: string;
    addedToFAQ?: boolean;
    questionOutput: AnswerReviewOutput;
    approvalModus: ApprovalModus;
  };

export const APPROVAL_MODUS = {
  AUTOMATIC: 'automatic',
  MANUAL: 'manual',
} as const;

export type ApprovalModus = ObjectValues<typeof APPROVAL_MODUS>;

export type QuestionReviewInitiatedEventProps = BaseMixpanelEvent &
  AhaMomentProps & {
    questionId: string;
    questionContent: string;
    response?: string;
    questionOutput: AnswerReviewOutput;
    approvalModus: ApprovalModus;
    questionAssignee: string;
  };

export const DR_TOUR_SOURCE = {
  CARD: 'card',
  GLOBAL: 'global',
} as const;

export type DrTourSource = ObjectValues<typeof DR_TOUR_SOURCE>;

type DataRoomTourBaseProps = BaseMixpanelEvent & {
  dataRoomId: string;
  dataRoomName: string;
  userSide: QAndASides;
};

export type DataRoomTourTakenEventProps = DataRoomTourBaseProps & {
  drTourStartPage: string;
  drTourSource: DrTourSource;
};

export type DataRoomTourCardViewedEventProps = DataRoomTourBaseProps & {
  drCardPage: string;
  drCardTimeSpent: number;
  drInTourMode: YesNo;
};

export type DataRoomTourHiddenForeverEventProps = DataRoomTourBaseProps;

export type DataRoomTourEnlargedEventProps = DataRoomTourBaseProps & {
  drCardPage: string;
  drTourSource: DrTourSource;
};

export const PASSWORD_FORGOTTEN_REROUTED_FROM = {
  LOGIN_PAGE: 'Login Page',
  SECURE_LINK_PAGE: 'Secure Link Page',
} as const;

export type PasswordForgottenReroutedFrom = ObjectValues<
  typeof PASSWORD_FORGOTTEN_REROUTED_FROM
>;

export type PasswordForgottenProps = BaseMixpanelEvent &
  EmailOrPhoneRequest & {
    reroutedFrom: PasswordForgottenReroutedFrom;
  };

export const LISTING_SHARE_MODE = {
  CLIPBOARD: 'Clipboard',
  FACEBOOK: 'Facebook',
  LINKEDIN: 'LinkedIn',
  EMAIL: 'Email',
  PREVIOUS_LEADS: 'Previous leads',
  CONTACTS: 'Contacts',
} as const;

export type ListingShareMode = ObjectValues<typeof LISTING_SHARE_MODE>;

export type ListingShareInitiatedEventProps = BaseMixpanelEvent & {
  listingId: string;
  listingName: string;
  listingShareMode: ListingShareMode;
};

export const DOWNLOAD_SOURCE = {
  DATA_ROOM_FOOTER: 'Data Room Footer',
  FILE_POPUP: 'File Popup',
} as const;

export type DownloadSource = ObjectValues<typeof DOWNLOAD_SOURCE>;

export const APP_OS = {
  [DESKTOP_APP_PLATFORM.WINDOWS]: 'Windows',
  [DESKTOP_APP_PLATFORM.LINUX]: 'Linux',
  [DESKTOP_APP_PLATFORM.MACOS]: 'Mac',
} as const;

export type AppOs = ObjectValues<typeof APP_OS>;

export type DesktopAppDownloadedEventProps = BaseMixpanelEvent & {
  downloadSource: DownloadSource;
  appOs: AppOs;
  dataRoomId: string;
  dataRoomName: string;
};

type FileSecurityBaseProps = BaseMixpanelEvent & {
  dataRoomId: string;
  dataRoomName: string;
};

export type FileSecurityVisitedEventProps = FileSecurityBaseProps;

export type MaxSecurityCardClickedEventProps = FileSecurityBaseProps;

export type MaxSecurityInterestRegisteredEventProps = FileSecurityBaseProps;

export const GROUP_FEATURE_DISCOVERED = {
  GROUP_ROW: 'Group Row',
  GROUP_NAME: 'Group Name',
} as const;

export type GroupFeatureDiscovered = ObjectValues<
  typeof GROUP_FEATURE_DISCOVERED
>;

export type GroupFeaturesDiscoveredEventProps = BaseMixpanelEvent & {
  featureDiscovered: GroupFeatureDiscovered;
};

export const MEMBER_ACTIVITY_PAGE = {
  LEAD_BOARD: 'Lead Board',
  MEMBERS_PAGE: 'Members Page',
} as const;

export type MemberActivityPage = ObjectValues<typeof MEMBER_ACTIVITY_PAGE>;

export const LISTING_CREATION_METHOD = {
  AI: 'ai',
  MANUAL: 'manual',
} as const;

export type ListingCreationMethod = ObjectValues<
  typeof LISTING_CREATION_METHOD
>;

export type ListingDocumentUploadedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dealName: string;
  listingType: string;
  // Always AI on this event - the manual path uploads no document. Sent anyway so
  // it can be filtered alongside the other listing creation events.
  creationMethod: ListingCreationMethod;
  // pdf, doc or docx - the dropzone accepts nothing else.
  fileFormat: string;
  fileSizeInBytes: number;
};

// The two LCW steps that hold AI-drafted fields, numbered as the event contract
// asks for them. Steps 3-5 draft nothing, so they report nothing.
export const AI_DRAFTED_FIELD_STEP = {
  BUSINESS_INFO: 1,
  STATEMENT_OF_INFORMATION: 2,
} as const;

export type AiDraftedFieldStep = ObjectValues<typeof AI_DRAFTED_FIELD_STEP>;

export type AiDraftedFieldEditedEventProps = BaseMixpanelEvent & {
  dealId: string;
  dealName: string;
  fieldName: string;
  step: AiDraftedFieldStep;
  listingName: string;
};

export type MemberActivityFilterUpdatedEventProps = BaseMixpanelEvent & {
  platformIncluded: boolean;
  notificationsIncluded: boolean;
  dataRoomIncluded: boolean;
  memberId: string;
  memberName: string;
  dealId: string;
  dealName: string;
  memberActivityPage: MemberActivityPage;
};

type EnterpriseReportBaseProps = BaseMixpanelEvent & {
  email: string;
  seniority?: string;
  enterpriseId: string;
  enterpriseName: string;
};

export type EnterpriseReportPopupOpenedEventProps =
  EnterpriseReportBaseProps & {
    enterpriseBusinessUnitCount: number;
    enterpriseMemberCount: number;
  };

export type EnterpriseReportGenerateClickedEventProps =
  EnterpriseReportBaseProps & {
    periodKey: string;
    periodStart: string;
    periodEnd: string;
  };

/**
 * Shared by the three "save chat attachments to the Data Room" toast events, which
 * all report the same thing: how many attachments the message carried, and which side
 * the sender is on.
 */
export type ChatAttachmentToastEventProps = BaseMixpanelEvent & {
  attachmentCount: number;
  userSide?: QAndASides;
};

export const AUTOMATION_RELEASE_MODE = {
  all_at_once: 'Everything at once',
  one_at_a_time: 'One at a time',
} as const;

export type AutomationReleaseMode = ObjectValues<
  typeof AUTOMATION_RELEASE_MODE
>;

export const AUTOMATION_TOUR_STEP_NAME = {
  LEAD_STAGES: 'Lead stages',
  FILES_LEADS_REACH: 'Files leads reach',
  FILES: 'Files',
  STAGE_RULES: 'Stage rules',
} as const;

export type AutomationTourStepName = ObjectValues<
  typeof AUTOMATION_TOUR_STEP_NAME
>;

type AutomationBaseProps = BaseMixpanelEvent & {
  dealId: string;
  dealName: string;
};

export type AutomationStageOpenedEventProps = AutomationBaseProps & {
  stageName: string;
  releaseMode: AutomationReleaseMode;
};

export type AutomationTourStartedEventProps = AutomationBaseProps & {
  releaseMode: AutomationReleaseMode;
};

export type AutomationTourStepViewedEventProps = AutomationBaseProps & {
  stepNumber: number;
  stepName: AutomationTourStepName;
};

export type AutomationTourCompletedEventProps = AutomationBaseProps;

export type AutomationTourClosedEventProps = AutomationBaseProps & {
  stepNumber: number;
};

export const PAUSED_LEAD_UNLOCK_ACTION = {
  UNLOCK_NEXT_ITEM: 'Unlock next item',
  COMPLETE_THE_ORDER: 'Complete the order',
} as const;

export type PausedLeadUnlockAction = ObjectValues<
  typeof PAUSED_LEAD_UNLOCK_ACTION
>;

export const PAUSED_LEAD_LOCATION = {
  LEAD_DRAWER: 'Lead drawer',
  MESSAGING: 'Messaging',
} as const;

export type PausedLeadLocation = ObjectValues<typeof PAUSED_LEAD_LOCATION>;

export const PAUSED_LEAD_REPLY_TAB = {
  PROFILE: 'Profile',
  ACTIVITY: 'Activity',
  FILES: 'Files',
  NOTES: 'Notes',
} as const;

export type PausedLeadReplyTab = ObjectValues<typeof PAUSED_LEAD_REPLY_TAB>;

export type PausedLeadMixpanelProps = AutomationBaseProps & {
  leadId: string;
  leadName: string;
  stageName?: string;
};

export type PausedLeadUnlockedEventProps = PausedLeadMixpanelProps & {
  action: PausedLeadUnlockAction;
  location: PausedLeadLocation;
};

export type PausedLeadReplyOpenedEventProps = PausedLeadMixpanelProps & {
  fromTab: PausedLeadReplyTab;
};

export const AUTOMATION_REMOVAL_SHARED_FROM = {
  SHARE_DRAWER: 'Share drawer',
  LEAD_DRAWER: 'Lead drawer',
} as const;

export type AutomationRemovalSharedFrom = ObjectValues<
  typeof AUTOMATION_REMOVAL_SHARED_FROM
>;

export type AutomationRemovalCancelledEventProps = AutomationBaseProps & {
  leadIds: string[];
  leadNames: string[];
  sharedFrom: AutomationRemovalSharedFrom;
};

export const DRAWER_NAVIGATION_DIRECTION = {
  PREVIOUS: 'previous',
  NEXT: 'next',
} as const;

export type DrawerNavigationDirection = ObjectValues<
  typeof DRAWER_NAVIGATION_DIRECTION
>;

export type DrawerRowNavigatedEventProps = BaseMixpanelEvent & {
  direction: DrawerNavigationDirection;
};

export type MixpanelEventProps =
  | LogoutProps
  | ErrorViewedProps
  | PageViewedProps
  | DocumentEventProps
  | WitnessedDocumentViewedEventProps
  | SearchInitiatedEventProps
  | SecureLinkCreationInitiatedEventProps
  | ListingCreationInitiatedEventProps
  | ListingVisitedEventProps
  | WitnessedListingVisitedEventProps
  | MobileVerificationInitiatedEventProps
  | EnquiryCreationInitiatedEventProps
  | EnquiryWithdrawalInitiatedEventProps
  | SignupInitiatedProps
  | DealCreationInitiatedEventProps
  | SoftSignalInitiatedEventProps
  | SoftSignalSeenEventProps
  | DocumentPublishedEventProps
  | QuestionSubmissionInitiatedEventProps
  | QuestionReplyInitiatedEventProps
  | AnswerReviewInitiatedEventProps
  | QuestionReviewInitiatedEventProps
  | PhoneNumberSetupInitiatedEventProps
  | DataRoomTourTakenEventProps
  | DataRoomTourCardViewedEventProps
  | DataRoomTourHiddenForeverEventProps
  | DataRoomTourEnlargedEventProps
  | PasswordForgottenProps
  | ListingShareInitiatedEventProps
  | DesktopAppDownloadedEventProps
  | FileSecurityVisitedEventProps
  | MaxSecurityCardClickedEventProps
  | MaxSecurityInterestRegisteredEventProps
  | GroupFeaturesDiscoveredEventProps
  | MemberActivityFilterUpdatedEventProps
  | EnterpriseReportPopupOpenedEventProps
  | EnterpriseReportGenerateClickedEventProps
  | ChatAttachmentToastEventProps
  | ListingDocumentUploadedEventProps
  | AiDraftedFieldEditedEventProps
  | AutomationStageOpenedEventProps
  | AutomationTourStartedEventProps
  | AutomationTourStepViewedEventProps
  | AutomationTourCompletedEventProps
  | AutomationTourClosedEventProps
  | PausedLeadUnlockedEventProps
  | PausedLeadReplyOpenedEventProps
  | AutomationRemovalCancelledEventProps
  | DrawerRowNavigatedEventProps;
